// Shared phone normalization. Client-safe (no server imports).
//
// The old behaviour blindly prefixed "+" to whatever was typed, so a US number
// entered as 10 digits became "+6192539140" which Twilio rejects. This module
// is the single source of truth for turning user input into E.164.

export type PhoneResult = { ok: true; e164: string } | { ok: false; reason: string };

/** Strip everything except digits and a leading plus. */
function clean(input: string): string {
  const trimmed = input.trim();
  const plus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  return plus ? `+${digits}` : digits;
}

/**
 * Normalize a phone number to E.164.
 * - 10 digits            -> assumed US/Canada, becomes +1XXXXXXXXXX
 * - 11 digits leading 1  -> becomes +1XXXXXXXXXX
 * - already +CC...       -> validated as-is
 * - anything else        -> rejected with a helpful reason
 */
export function normalizePhone(input: string | null | undefined): PhoneResult {
  if (!input || !input.trim()) return { ok: false, reason: "Enter a phone number." };
  const c = clean(input);

  if (c.startsWith("+")) {
    const digits = c.slice(1);
    if (!/^[1-9]\d{7,14}$/.test(digits)) {
      return { ok: false, reason: "That international number does not look right. Example: +15551234567" };
    }
    return { ok: true, e164: `+${digits}` };
  }

  if (/^\d{10}$/.test(c)) {
    if (c[0] === "0" || c[0] === "1") {
      return { ok: false, reason: "US numbers start with the area code. Example: 619 555 0123" };
    }
    return { ok: true, e164: `+1${c}` };
  }

  if (/^1\d{10}$/.test(c)) return { ok: true, e164: `+${c}` };

  return {
    ok: false,
    reason: "Enter a 10-digit US number, or include the country code (example: +447700900123).",
  };
}

/** True when the stored value is already a valid E.164 string. */
export function isE164(value: string | null | undefined): boolean {
  return !!value && /^\+[1-9]\d{7,14}$/.test(value);
}

/** Best-effort normalize; returns null instead of an error object. */
export function toE164OrNull(input: string | null | undefined): string | null {
  const r = normalizePhone(input ?? "");
  return r.ok ? r.e164 : null;
}

/** Pretty display for US numbers, passthrough for everything else. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : value;
}
